/* eslint-disable react/jsx-indent */
import { Box, IconButton, TableCell, TableRow, Tooltip, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import React, { FC } from 'react';
import NumberField from 'components/number-field';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ListingEntry } from 'graphql/listings/queries';
import { ChargeFormEntry } from '../../../types';
import { DescriptionField } from './Fields/DescriptionField';
import { PayeeField } from './Fields/PayeeField';
import { TaxField } from './Fields/TaxField';
import { AmountField } from './Fields/AmountField';
import { ChargesFormType } from '../../../hooks';
import { formatMoney } from 'utils/helpers';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import { EndorsementCodeField } from './Fields/EndorsementCodeField';

const useStyles = makeStyles((theme) => ({
  table: {
    padding: `${theme.spacing(2)}`,
  },
  searchContact: {
    '& .MuiInputLabel-filled': {
      color: theme.palette.primary.dark,
    },
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      border: 'unset',
      borderRadius: '4px',

      '&.MuiFilledInput-underline::before': {
        content: 'unset',
      },

      '&.MuiFilledInput-underline::after': {
        content: 'unset',
      },
    },
  },
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      fontSize: '13px',
      display: 'flex',
      justifyContent: 'flex-end',

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        width: '4rem',
        padding: '8px 6px',
      },
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      borderRadius: '5px',
      color: theme.palette.primary.dark,
      fontSize: '0.8rem',

      '& .MuiOutlinedInput-input': {
        padding: '10px !important',
      },

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
    },
  },
  numberInput: {
    '& .MuiOutlinedInput-root': {
      width: '35px',
      height: '35px',
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '5px',
      color: theme.palette.primary.dark,

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        padding: `${theme.spacing(0.25)}`,
        textAlign: 'center',
      },
    },
  },
  numberContainer: {
    width: '30px',
    height: '30px',
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '5px',
    color: theme.palette.primary.dark,
  },
  tableChargeRow: {
    padding: `8px ${theme.spacing(0.5)}`,
    fontSize: '0.8rem',
  },
  tableMenuRow: {
    paddingLeft: '2px',
    paddingRight: '2px',
  },
  payerActionCell: {
    textAlign: 'center',
  },
  payerIcon: {
    fontSize: theme.typography.body2.fontSize,
  },
  options: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: '5px',
  },
  iconButton: {
    minWidth: theme.spacing(5.5),
    minHeight: theme.spacing(5.5),
    [theme.breakpoints.up('md')]: {
      minWidth: 'unset',
      minHeight: 'unset',
    },
  },
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
}));

interface Props {
  charge: ChargeFormEntry;
  index: number;
  currentLetter?: string;
  lettersPerMonth: string[];
  textPredectiveCharges: ListingEntry[];
  formatedAccountingData: ChargeFormEntry[];
  hideTax: boolean;
  isOrderTypeNotHUD1: boolean;
  onDeleteCharge: (rowIndex: number) => void;
  onEditCharge: (charge: ChargeFormEntry, rowIndex: number) => void;
  onEditSplitAmount: (charge: ChargeFormEntry, kind: 'Payee' | 'Payer' | 'Tax') => void;
  isPartiesInvolvedRowOpen: boolean;
  togglePartiesinvolvedRowOpen: () => void;
  onChargeAmountChange: (newAmount: number | null) => void;
  onChargePaidByAmountChange: () => void;
  onChargeNumberOfMonthsChange: (newMonths: number) => void;
  onPerMonthChange: (newPerMonthAmount: number) => void;
}

export const ChargeRow: FC<Props> = (props) => {
  const {
    charge,
    index,
    textPredectiveCharges,
    currentLetter,
    lettersPerMonth,
    hideTax,
    isOrderTypeNotHUD1,
    onDeleteCharge,
    onEditSplitAmount,
    isPartiesInvolvedRowOpen,
    togglePartiesinvolvedRowOpen,
    onChargeAmountChange,
    onChargePaidByAmountChange,
    onChargeNumberOfMonthsChange,
    onPerMonthChange,
  } = props;
  const { getValues, control } = useFormContext<ChargesFormType>();
  const { hideEndorsementCode } = useGeneralChargesContext();

  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const showOverrideIndicator =
    charge.kind !== 'manual' &&
    charge.amount_calculated !== 0 &&
    charge.amount_override !== 0 &&
    charge.amount_calculated !== charge.amount_override;

  const handleDeleteCharge = () => {
    onDeleteCharge(index);
  };

  return (
    <TableRow className={classes.root}>
      {/* Options */}
      <TableCell className={classes.tableMenuRow}>
        <Box className={classes.options}>
          <IconButton aria-label="expand row" size="small" className={classes.iconButton} onClick={togglePartiesinvolvedRowOpen}>
            <FontAwesomeIcon
              icon={isPartiesInvolvedRowOpen ? 'chevron-up' : 'chevron-down'}
              size="sm"
            />
          </IconButton>

          {charge.kind === 'manual' && (
            <IconButton aria-label="delete charge" size="small" className={classes.iconButton} onClick={handleDeleteCharge}>
              <FontAwesomeIcon
                icon="trash-alt"
                size="sm"
              />
            </IconButton>
          )}
        </Box>
      </TableCell>

      {/* Number */}
      <>
        {charge.kind === 'manual' ? (
          <TableCell className={classes.tableChargeRow}>
            <Controller
              name={`charges.${index}.number`}
              control={control}
              render={({ field: { ref, onChange, ...field } }) => (
                <NumberField
                  {...field}
                  customRef={ref}
                  color="secondary"
                  className={classes.numberInput}
                  variant="outlined"
                  onValueChange={(prop) => {
                    onChange(prop.floatValue);
                  }}
                  value={getValues(`charges.${index}.number`)}
                />
              )}
            />
          </TableCell>
        ) : (
          <TableCell className={classes.tableChargeRow}>
            <Box className={classes.numberContainer} style={{ width: '35px' }}>
              {`${(hideEndorsementCode || (charge.letter === "")) ? "" : `${charge.letter}-`}${charge.number}`}
            </Box>
          </TableCell>
        )}
      </>

      {/* Endorsement Code */}
      {hideEndorsementCode === false && (
        <EndorsementCodeField
          index={index}
          charge={charge}
        />
      )}

      {/* Description */}
      <DescriptionField
        index={index}
        charge={charge}
        textPredectiveCharges={textPredectiveCharges}
      />

      {/* Payee */}
      <TableCell className={classes.tableChargeRow}>
        <PayeeField charge={charge} onOpenSplitModal={() => onEditSplitAmount(charge, 'Payee')} />
      </TableCell>

      <TableCell />

      {/* Amount */}
      <TableCell className={classes.tableChargeRow}>
        <Tooltip
          title={`Original calculation: ${formatMoney(charge.amount_calculated)}`}
          arrow
          placement="top"
          disableFocusListener={!showOverrideIndicator}
          disableHoverListener={!showOverrideIndicator}
          disableTouchListener={!showOverrideIndicator}
        >
          <div>
            <AmountField
              {...props}
              name="amount"
              disabled={charge.disabled}
              onAmountChange={onChargeAmountChange}
              borderColor={showOverrideIndicator ? theme.palette.warning.main : undefined}
            />
          </div>
        </Tooltip>
      </TableCell>

      {/* Per Month, Months */}
      {currentLetter && lettersPerMonth.includes(currentLetter) && (
        <>
          <TableCell className={classes.tableChargeRow}>
            <AmountField
              {...props}
              name="per_month"
              disabled={charge.disabled}
              onAmountChange={(value) => onPerMonthChange(value || 0)}
            />
          </TableCell>

          <TableCell className={classes.tableChargeRow}>
            <Controller
              name={`charges.${index}.months`}
              control={control}
              disabled={charge.disabled}
              render={({ field: { ref, onChange, ...field } }) => (
                <NumberField
                  {...field}
                  customRef={ref}
                  color="secondary"
                  className={classes.numberField}
                  variant="outlined"
                  disabled={field.disabled}
                  onValueChange={(prop) => {
                    onChange(prop.floatValue);
                    onChargeNumberOfMonthsChange(prop.floatValue || 0);
                  }}
                  value={getValues(`charges.${index}.months`)}
                />
              )}
            />
          </TableCell>
        </>
      )}

      {/* Tax */}
      {!hideTax && (
        <TableCell className={classes.tableChargeRow}>
          <TaxField charge={charge} onOpenSplitModal={() => onEditSplitAmount(charge, 'Tax')} />
        </TableCell>
      )}

      {/* Buyer At */}
      <TableCell
        className={classes.tableChargeRow}
        style={{ borderLeft: `1px solid ${theme.palette.divider}`, paddingLeft: '5px' }}
      >
        <AmountField
          {...props}
          name="buyer_at"
          disabled={charge.disabled}
          onAmountChange={onChargePaidByAmountChange}
        />
      </TableCell>

      {/* Buyer Before */}
      {isOrderTypeNotHUD1 && (
        <TableCell
          className={classes.tableChargeRow}
          style={{
            borderRight: `1px solid ${theme.palette.divider}`,
            paddingRight: '5px',
          }}
        >
          <AmountField
            {...props}
            name="buyer_before"
            disabled={charge.disabled}
            onAmountChange={onChargePaidByAmountChange}
          />
        </TableCell>
      )}

      {/* Selleer At */}
      <TableCell className={classes.tableChargeRow}>
        <AmountField
          {...props}
          name="seller_at"
          disabled={charge.disabled}
          onAmountChange={onChargePaidByAmountChange}
        />
      </TableCell>

      {/* Seller Before */}
      {isOrderTypeNotHUD1 && (
        <TableCell
          className={classes.tableChargeRow}
          style={{
            borderRight: `1px solid ${theme.palette.divider}`,
            paddingRight: '5px',
          }}
        >
          <AmountField
            {...props}
            name="seller_before"
            disabled={charge.disabled}
            onAmountChange={onChargePaidByAmountChange}
          />
        </TableCell>
      )}

      {/* By Others */}
      {isOrderTypeNotHUD1 && (
        <TableCell className={classes.tableChargeRow}>
          <AmountField
            {...props}
            name="by_others"
            disabled={charge.disabled}
            onAmountChange={onChargePaidByAmountChange}
          />
        </TableCell>
      )}

      {/* Select Payers */}
      {isOrderTypeNotHUD1 && (
        <TableCell className={`${classes.tableMenuRow} ${classes.payerActionCell}`}>
          {!charge.disabled && (
            <IconButton
              aria-label={t('orders:payersLabel')}
              size="small"
              className={classes.iconButton}
              onClick={() => onEditSplitAmount(charge, 'Payer')}
            >
              <FontAwesomeIcon icon="user" className={classes.payerIcon} />
            </IconButton>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};
